import { Ref, forwardRef } from "react"
import { Form, Formik, FormikHelpers, FormikProps } from "formik"
import * as yup from "yup"
import { CreateUpdateSurveyFormContent } from "./CreateUpdateSurveyFormContent"
import { CreateOrEditSurveyFormValues, MerchantSurvey } from "../merchantSurveyTypes"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"

type CreateOrEditSurveyFormProps = Readonly<{
    initialValues?: CreateOrEditSurveyFormValues | null
    onSubmit: (formData: MerchantSurvey) => void
}>
type FormikRef = Ref<FormikProps<CreateOrEditSurveyFormValues>>

export const CreateOrEditSurveyForm = forwardRef(
    ({ initialValues, onSubmit }: CreateOrEditSurveyFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()
        const dateTimeFactory = useDateTime()
        const today = dateTimeFactory.now()
        const defaultTime = dateTimeFactory.fromISO("13:00")

        const defaultInitialValues = {
            title: "",
            description: "",
            questions: [],
            isActive: true,
            activePeriod: {
                from: {
                    date: today,
                },
                to: {
                    date: null,
                },
            },
            card: {
                visibility: {
                    timeOfDay: defaultTime,
                    expirationDays: 2,
                },
            },
        }

        const validationSchema = yup.object({
            title: yup.string().required(l10n.getString("create-or-edit-merchant-survey-field-name-required")),
            questions: yup.array().min(1, l10n.getString("create-or-edit-merchant-survey-field-questions-minError")),
        })

        function handleSubmit(
            values: CreateOrEditSurveyFormValues,
            _helpers: FormikHelpers<CreateOrEditSurveyFormValues>,
        ) {
            const modifySurvey = {
                ...values,
                activePeriod: {
                    from: {
                        date: values.activePeriod?.from?.date.toISODate(),
                    },
                    to: {
                        date: values.activePeriod?.to?.date?.toISODate() ?? null,
                    },
                },
                card: {
                    visibility: {
                        timeOfDay: values.card.visibility.timeOfDay
                            ? values.card.visibility.timeOfDay.toFormat("HH:mm")
                            : null,
                        expirationDays: Number(values.card.visibility.expirationDays),
                    },
                },
            }
            onSubmit(modifySurvey)
        }

        return (
            <Formik<CreateOrEditSurveyFormValues>
                enableReinitialize
                initialValues={initialValues ? initialValues : defaultInitialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                innerRef={ref}
            >
                <Form>
                    <CreateUpdateSurveyFormContent />
                </Form>
            </Formik>
        )
    },
)
